import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { canViewPage } from '@cls/shared';
import { useAuth } from '../../features/auth/AuthProvider.js';
import { api } from '../../lib/api.js';
import { DeployModal } from './DeployModal.js';
import { RealMap, type RealMapPin, type RealMapLine } from '../primitives/RealMap.js';

interface NearbyProject {
  id: string;
  code: string;
  name: string;
  postcode: string | null;
  status: string;
  lat: number;
  lng: number;
  distanceKm: number;
  assigned: boolean;
}

interface NearbyWorker {
  id: string;
  name: string;
  postcode: string;
  lat: number | null;
  lng: number | null;
  assignedProjectIds: string[];
}

interface NearbyResponse {
  worker: NearbyWorker;
  items: NearbyProject[];
}

interface Props {
  onClose: () => void;
  workerId: string;
  workerName: string;
  workerPhone?: string | null;
  workerPostcode?: string | null;
}

function avatarHue(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360}, 60%, 55%)`;
}

function distColor(km: number): string {
  if (km < 20) return 'var(--live)';
  if (km < 60) return 'var(--warn)';
  return 'var(--muted)';
}

export function ProximityModal({ onClose, workerId, workerName, workerPhone, workerPostcode }: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canOpenProject = !!user && canViewPage(user, 'projects');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deployFor, setDeployFor] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data } = useQuery({
    queryKey: ['proximity', workerId],
    queryFn: () => api.get<NearbyResponse>(`/workers-near/worker/${workerId}/projects`).then((r) => r.data),
  });

  const worker = data?.worker;
  const items = data?.items ?? [];
  const hasWorkerCoords = worker?.lat != null && worker?.lng != null;

  const goToProject = (projectId: string) => {
    if (!canOpenProject) return;
    navigate(`/projects?open=${encodeURIComponent(projectId)}`);
    onClose();
  };

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 640 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title" id="prox-title">📍 {workerName} — Project Proximity</div>
        <div className="modal-sub" id="prox-sub">Home: {workerPostcode ?? worker?.postcode ?? 'unknown'}</div>

        <div className="prox-map" id="prox-map" style={{ height: 200, background: '#0A0F1E', border: '1px solid var(--border)', borderRadius: 10, marginBottom: 11, position: 'relative', overflow: 'hidden' }}>
          {hasWorkerCoords ? (() => {
            const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
            const lines: RealMapLine[] = items.map((p) => ({
              id: p.id,
              from: { lat: worker!.lat!, lng: worker!.lng! },
              to: { lat: p.lat, lng: p.lng },
              color: 'rgba(232,164,39,.18)',
              weight: 0.7,
              dashArray: '2,2',
            }));
            const pins: RealMapPin[] = items.map((p) => {
              const isSelected = selectedId === p.id;
              const dotSize = isSelected ? 14 : 10;
              const bg = p.status === 'live' ? 'var(--live)' : 'var(--done)';
              const border = isSelected ? '2px solid var(--accent)' : '2px solid rgba(255,255,255,.5)';
              const shadow = isSelected ? '0 0 0 3px rgba(245,158,11,.35)' : 'none';
              return {
                id: p.id,
                lat: p.lat,
                lng: p.lng,
                title: `${p.code} — ${p.name} · ${p.distanceKm.toFixed(1)}km`,
                iconHtml: `<div style="display:flex;flex-direction:column;align-items:center;gap:2px"><div style="font-size:7px;font-family:'Syne', sans-serif;font-weight:700;color:var(--accent);white-space:nowrap;background:rgba(11,15,26,.9);padding:1px 3px;border-radius:2px;line-height:1">${esc(p.code)}</div><div style="width:${dotSize}px;height:${dotSize}px;border-radius:50%;background:${bg};border:${border};box-shadow:${shadow};transition:all .12s ease"></div></div>`,
                iconSize: [44, 28],
                iconAnchor: [22, 21],
                zIndexOffset: isSelected ? 200 : 100,
              };
            });
            pins.push({
              id: `__worker__${worker!.id}`,
              lat: worker!.lat!,
              lng: worker!.lng!,
              title: workerName,
              iconHtml: `<div style="width:12px;height:12px;border-radius:50%;background:${avatarHue(workerName)};border:2px solid white"></div>`,
              iconSize: [16, 16],
              iconAnchor: [8, 8],
              zIndexOffset: 300,
            });
            return (
              <RealMap
                pins={pins}
                lines={lines}
                height="100%"
                onPinClick={(id) => {
                  if (id.startsWith('__worker__')) return;
                  setSelectedId(id);
                }}
              />
            );
          })() : (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--faint)', fontSize: 11 }}>
              No coordinates for this worker — set a valid postcode to enable the map.
            </div>
          )}
        </div>

        <div id="prox-list">
          {items.length === 0 ? (
            <div style={{ color: 'var(--muted)', fontSize: 12, textAlign: 'center', padding: 16 }}>No nearby projects found.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {items.map((p) => {
                const isSelected = selectedId === p.id;
                const borderColor = isSelected ? 'var(--accent)' : p.assigned ? 'var(--accent)' : 'var(--border)';
                const bg = isSelected ? 'rgba(245,158,11,.12)' : 'var(--card)';
                return (
                  <div
                    key={p.id}
                    role={canOpenProject ? 'button' : undefined}
                    tabIndex={canOpenProject ? 0 : undefined}
                    onClick={() => canOpenProject && goToProject(p.id)}
                    onKeyDown={(e) => { if (canOpenProject && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); goToProject(p.id); } }}
                    onMouseEnter={() => setSelectedId(p.id)}
                    title={canOpenProject ? `Open ${p.code} — ${p.name}` : undefined}
                    style={{
                      background: bg,
                      border: `1px solid ${borderColor}`,
                      borderRadius: 8,
                      padding: 10,
                      cursor: canOpenProject ? 'pointer' : 'default',
                      transition: 'background .12s ease, border-color .12s ease',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                      <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 10, color: 'var(--accent)' }}>{p.code}</span>
                      <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 12, color: distColor(p.distanceKm) }}>
                        {p.distanceKm.toFixed(1)}<span style={{ fontSize: 8, color: 'var(--muted)' }}>km</span>
                      </span>
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 500, marginBottom: 2 }}>{p.name}</div>
                    <div style={{ fontSize: 9, color: 'var(--muted)' }}>📍{p.postcode ?? '—'}</div>
                    {p.assigned ? (
                      <div style={{ marginTop: 4 }}>
                        <span className="badge b-deployed" style={{ fontSize: 8 }}>assigned</span>
                      </div>
                    ) : (
                      <div style={{ marginTop: 4 }}>
                        <button
                          className="btn btn-primary btn-xs"
                          style={{ width: '100%', justifyContent: 'center' }}
                          onClick={(e) => { e.stopPropagation(); setDeployFor({ id: p.id, name: p.name }); }}
                        >
                          + Deploy
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="modal-actions"><button className="btn btn-ghost" onClick={onClose}>Close</button></div>
      </div>
      {deployFor ? (
        <DeployModal
          onClose={() => setDeployFor(null)}
          workerId={workerId}
          workerName={workerName}
          workerPhone={workerPhone ?? ''}
          projectId={deployFor.id}
          projectName={deployFor.name}
        />
      ) : null}
    </div>,
    document.body,
  );
}
